"use client"

import { Suspense, useEffect, useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

/**
 * Client-side banner that surfaces when Stripe sends a buyer back to
 * the marketing page with `?canceled=1` (checkout abandon path).
 *
 * Why client-side? Reading `searchParams` from a server component
 * opts the entire route into dynamic rendering — `export const
 * revalidate = N` becomes a no-op and every request hits the origin.
 * The marketing page is the highest-traffic surface for the framework
 * SKU; dynamic rendering on every view would erase the ISR cache
 * benefit for a UX nicety. Keeping the page static + reading the
 * search param in a hydrated banner restores the static-marketing
 * caching profile while preserving the cancelled-state acknowledgement
 * Stripe abandoners need.
 *
 * SEO note: when `?canceled=1` is the URL the user lands on, the
 * server-rendered HTML still emits `<link rel="canonical">` pointing
 * at the bare URL (set in page-level metadata), which is how Google
 * consolidates the parameterised variant back to the canonical. We
 * intentionally do NOT set `noindex` on `?canceled=1` — the canonical
 * is the strong signal Google honours.
 */
interface CheckoutCancelledBannerProps {
  /**
   * Trailing sentence after the no-charge acknowledgement. Defaults
   * to the framework-page copy; the skills page overrides with
   * "read on first" since there's no framework to browse on that
   * surface.
   */
  trailingHint?: string
}

function CheckoutCancelledBannerInner({
  trailingHint = "you can buy below or browse the framework first.",
}: CheckoutCancelledBannerProps): JSX.Element | null {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  // Capture the cancelled state ONCE at mount in local component
  // state. The visible banner is then gated on local state, not on
  // the live `searchParams` value.
  //
  // Why: the URL-cleanup effect below strips `?canceled=1` (so
  // bookmarking from the cancelled view captures the canonical URL),
  // which causes Next's `useSearchParams` to re-emit without the
  // param. If the banner's visibility were derived from
  // `searchParams.get("canceled")` directly, the very same effect
  // that cleans the URL would unmount the banner the user just
  // landed to see. Local state decouples "did this session start
  // cancelled?" from "what's the current query string?", so the
  // acknowledgement stays visible until the user dismisses it (or
  // navigates).
  const [isCanceled, setIsCanceled] = useState<boolean>(
    () => searchParams.get("canceled") === "1",
  )

  useEffect(() => {
    if (searchParams.get("canceled") === "1") {
      router.replace(pathname, { scroll: false })
    }
    // Run once on mount; subsequent `searchParams` changes after the
    // replace are intentional and shouldn't re-trigger cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!isCanceled) return null
  return (
    <div
      role="status"
      className="mb-8 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <p>
          <strong>Checkout cancelled.</strong> No charge was made. Come back any time —{" "}
          {trailingHint}
        </p>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={() => setIsCanceled(false)}
          className="-mt-0.5 shrink-0 rounded p-1 text-amber-200/80 hover:text-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
        >
          ×
        </button>
      </div>
    </div>
  )
}

/**
 * Suspense-wrapped export. `useSearchParams()` in a client component
 * requires a Suspense boundary above it for Next.js to allow the
 * surrounding route to stay statically rendered. Without this wrapper
 * the build fails with "useSearchParams should be wrapped in a
 * suspense boundary". The fallback is `null` because there's nothing
 * to show pre-hydration — the banner is purely a post-checkout-
 * abandon UX surface.
 */
export function CheckoutCancelledBanner(
  props: CheckoutCancelledBannerProps = {},
): JSX.Element {
  return (
    <Suspense fallback={null}>
      <CheckoutCancelledBannerInner {...props} />
    </Suspense>
  )
}
